import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import AdminLoading from './AdminLoading';
import AdminEmptyState from './AdminEmptyState';
import { IoGiftOutline } from 'react-icons/io5';

export default function AdminCoupons() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    code: '', discount_type: 'percent', discount_value: '', min_order_paise: '', valid_from: '', valid_to: '', max_uses: '',
  });

  const load = () => {
    api('/api/admin/coupons').then(setList).catch((e) => setError(e.message)).finally(() => setLoading(false));
  };

  useEffect(() => load(), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const rawVal = Number(form.discount_value) || 0;
    const payload = {
      code: form.code.trim().toUpperCase(),
      discount_type: form.discount_type,
      discount_value: form.discount_type === 'fixed' ? rawVal * 100 : rawVal,
      min_order_paise: form.min_order_paise ? Number(form.min_order_paise) * 100 : null,
      valid_from: form.valid_from || null,
      valid_to: form.valid_to || null,
      max_uses: form.max_uses ? Number(form.max_uses) : null,
    };
    try {
      if (editing) {
        await api(`/api/admin/coupons/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        setEditing(null);
      } else {
        await api('/api/admin/coupons', { method: 'POST', body: JSON.stringify(payload) });
      }
      setForm({ code: '', discount_type: 'percent', discount_value: '', min_order_paise: '', valid_from: '', valid_to: '', max_uses: '' });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (c) => {
    setEditing(c);
    setForm({
      code: c.code || '',
      discount_type: c.discount_type || 'percent',
      discount_value: c.discount_type === 'fixed' ? (c.discount_value / 100) : (c.discount_value ?? ''),
      min_order_paise: c.min_order_paise != null ? String(c.min_order_paise / 100) : '',
      valid_from: c.valid_from || '',
      valid_to: c.valid_to || '',
      max_uses: c.max_uses != null ? String(c.max_uses) : '',
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await api(`/api/admin/coupons/${id}`, { method: 'DELETE' });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const formatVal = (c) => (c.discount_type === 'percent' ? `${c.discount_value}%` : `₹${(c.discount_value / 100).toFixed(0)}`);

  if (loading) return <AdminLoading />;

  return (
    <Card>
      <CardHeader title="Coupons" />
      <CardContent>
        {error && <Typography color="error" className="admin-error" sx={{ mb: 2 }}>{error}</Typography>}
        <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <TextField size="small" label="Code" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} required sx={{ minWidth: 120 }} />
          <TextField size="small" select SelectProps={{ native: true }} label="Type" value={form.discount_type} onChange={(e) => setForm((f) => ({ ...f, discount_type: e.target.value }))} sx={{ minWidth: 100 }}>
            <option value="percent">Percent</option>
            <option value="fixed">Fixed (₹)</option>
          </TextField>
          <TextField size="small" type="number" label={form.discount_type === 'percent' ? 'Value %' : 'Value (₹)'} value={form.discount_value} onChange={(e) => setForm((f) => ({ ...f, discount_value: e.target.value }))} required sx={{ minWidth: 100 }} />
          <TextField size="small" type="number" label="Min order (₹)" value={form.min_order_paise} onChange={(e) => setForm((f) => ({ ...f, min_order_paise: e.target.value }))} sx={{ minWidth: 110 }} />
          <TextField size="small" type="date" label="Valid from" value={form.valid_from} onChange={(e) => setForm((f) => ({ ...f, valid_from: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
          <TextField size="small" type="date" label="Valid to" value={form.valid_to} onChange={(e) => setForm((f) => ({ ...f, valid_to: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
          <TextField size="small" type="number" label="Max uses" value={form.max_uses} onChange={(e) => setForm((f) => ({ ...f, max_uses: e.target.value }))} sx={{ minWidth: 90 }} />
          <Button type="submit" variant="contained">{editing ? 'Update' : 'Add'}</Button>
          {editing && <Button type="button" onClick={() => setEditing(null)}>Cancel</Button>}
        </Box>
        {list.length === 0 ? (
          <AdminEmptyState message="No coupons. Add one above." icon={IoGiftOutline} />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Min order</TableCell>
                  <TableCell>Valid from</TableCell>
                  <TableCell>Valid to</TableCell>
                  <TableCell>Uses</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell><strong>{c.code}</strong></TableCell>
                    <TableCell>{c.discount_type}</TableCell>
                    <TableCell>{formatVal(c)}</TableCell>
                    <TableCell>{c.min_order_paise != null ? `₹${(c.min_order_paise / 100).toFixed(0)}` : '–'}</TableCell>
                    <TableCell>{c.valid_from || '–'}</TableCell>
                    <TableCell>{c.valid_to || '–'}</TableCell>
                    <TableCell>{c.used_count}{c.max_uses != null ? ` / ${c.max_uses}` : ''}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => handleEdit(c)}>Edit</Button>
                      <Button size="small" color="error" onClick={() => handleDelete(c.id)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
